import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { UntypedFormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import {
  MatTable,
  MatTableDataSource,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { IndexTemplateComponent } from '@bisual/ngx-index-template';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { MessagingConversation } from 'src/app/models/messaging_conversation.model';
import { ArrayResponse } from 'src/app/models/array_response.interface';
import { ApiService } from 'src/app/services/api.service';
import { EmployeeService } from 'src/app/services/employee.service';
import { UtilsService } from 'src/app/services/utils.service';
import {
  AssignMessagingConversationDialogComponent,
  AssignMessagingConversationDialogResult,
} from './assign-messaging-conversation-dialog/assign-messaging-conversation-dialog.component';

@Component({
  selector: 'app-messaging-conversations',
  templateUrl: './messaging-conversations.component.html',
  styleUrls: ['./messaging-conversations.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatButton,
    MatIconButton,
    MatIcon,
    MatPaginator,
    MatFormField,
    MatLabel,
    MatInput,
    MatSelect,
    MatOption,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
  ],
})
export class MessagingConversationsComponent extends IndexTemplateComponent implements OnInit {
  private readonly api = inject(ApiService);
  private readonly employee = inject(EmployeeService);
  private readonly dialog = inject(MatDialog);
  private readonly change_detector_ref = inject(ChangeDetectorRef);

  displayedColumns: string[] = ['display_name', 'provider', 'space_type', 'assignment', 'actions'];
  dataSource = new MatTableDataSource<MessagingConversationSerialized>([]);
  syncing = false;

  override filterFormExtraParams: Record<string, unknown> = {
    search: [''],
    assignment_filter: ['pending'],
    space_type: [''],
  };

  constructor() {
    super(inject(Router), inject(UntypedFormBuilder), inject(ActivatedRoute), inject(UtilsService));
  }

  override ngOnInit(): void {
    const url_page = this.activatedRoute.snapshot.queryParamMap.get('page');
    if (url_page) {
      this.pageIndex = parseInt(url_page, 10) - 1;
    } else {
      this.pageIndex = 0;
    }

    const url_per_page = this.activatedRoute.snapshot.queryParamMap.get('per_page');
    if (url_per_page) {
      this.pageSize = parseInt(url_per_page, 10);
    }

    super.ngOnInit();
    this.filterForm.controls['search'].valueChanges.pipe(debounceTime(300), distinctUntilChanged()).subscribe((): void => {
      this.pageIndex = 0;
      this.fetchData();
    });
  }

  override fetchData(): void {
    const params: Record<string, string | number | boolean> = {
      page: this.pageIndex + 1,
      per_page: this.pageSize,
      order_by: 'created_at:desc',
      with: 'user,company',
    };

    const search = this.filterForm.value.search;
    if (typeof search === 'string' && search !== '') {
      params['search'] = search;
    }

    const scopes: string[] = [];
    const assignment_filter = this.filterForm.value.assignment_filter;
    if (assignment_filter === 'pending') {
      scopes.push('pendingAssignment');
    } else if (assignment_filter === 'ignored') {
      params['ignored_for_assignment'] = true;
    }

    if (scopes.length > 0) {
      params['scopes'] = scopes.join(',');
    }

    this.api.getMessagingConversations(params).subscribe((response: ArrayResponse<MessagingConversation>): void => {
      this.dataSource.data = response.data.map(conversation => this.serializeConversation(conversation));
      this.setMetadata(+response.meta.total, response.meta.current_page - 1, response.meta.per_page);
      this.change_detector_ref.markForCheck();
    });
  }

  override changePage(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.fetchData();
    super.changePage(event);
  }

  sync(): void {
    if (this.syncing) {
      return;
    }

    this.syncing = true;
    this.api.syncMessagingConversations().subscribe({
      next: (): void => {
        this.syncing = false;
        this.employee.refreshActionsRequired();
        this.fetchData();
      },
      error: (): void => {
        this.syncing = false;
        this.change_detector_ref.markForCheck();
      },
    });
  }

  openAssignDialog(row: MessagingConversationSerialized): void {
    this.dialog
      .open(AssignMessagingConversationDialogComponent, {
        width: '520px',
        data: { conversation: row },
      })
      .afterClosed()
      .subscribe((result: AssignMessagingConversationDialogResult | undefined): void => {
        if (result == null) {
          return;
        }

        this.employee.refreshActionsRequired();
        this.fetchData();
      });
  }

  unassign(row: MessagingConversationSerialized): void {
    this.api.unassignMessagingConversation(row.id).subscribe((): void => {
      this.employee.refreshActionsRequired();
      this.fetchData();
    });
  }

  ignoreForAssignment(row: MessagingConversationSerialized): void {
    this.api.ignoreMessagingConversationAssignment(row.id).subscribe((): void => {
      this.employee.refreshActionsRequired();
      this.fetchData();
    });
  }

  unignoreForAssignment(row: MessagingConversationSerialized): void {
    this.api.updateMessagingConversation(row.id, { ignored_for_assignment: false }).subscribe((): void => {
      this.employee.refreshActionsRequired();
      this.fetchData();
    });
  }

  private serializeConversation(conversation: MessagingConversation): MessagingConversationSerialized {
    let assignment_label = 'Pendiente de asignar';
    if (conversation.ignored_for_assignment) {
      assignment_label = 'Ignorada para asignación';
    } else if (conversation.user != null) {
      assignment_label = conversation.user.full_name ?? `${conversation.user.name ?? ''}`.trim();
    } else if (conversation.company != null) {
      assignment_label = conversation.company.fact_name ?? `Empresa #${conversation.company_id}`;
    } else if (conversation.company_id != null) {
      assignment_label = `Empresa #${conversation.company_id}`;
    }

    const is_unassigned = conversation.user_id == null && conversation.company_id == null;
    const is_pending_assignment = is_unassigned && !conversation.ignored_for_assignment;
    const space_type = messagingConversationSpaceTypeKey(conversation);
    const is_personal = isMessagingConversationPersonal(conversation);

    return {
      ...conversation,
      external_id: conversation.google_chat_space_name ?? conversation.slack_channel_id ?? conversation.teams_channel_id ?? '',
      provider_label: messagingConversationProviderLabel(conversation.provider),
      space_type,
      assignment_label,
      space_type_label: messagingConversationSpaceTypeLabel(conversation),
      space_type_chip_class: is_personal ? 'space-type-chip--personal' : 'space-type-chip--group',
      is_unassigned,
      is_pending_assignment,
      can_ignore_for_assignment: is_unassigned && !conversation.ignored_for_assignment,
      can_unignore_for_assignment: is_unassigned && conversation.ignored_for_assignment,
    };
  }
}

export interface MessagingConversationSerialized extends MessagingConversation {
  external_id: string;
  provider_label: string;
  space_type: string;
  assignment_label: string;
  space_type_label: string;
  space_type_chip_class: string;
  is_unassigned: boolean;
  is_pending_assignment: boolean;
  can_ignore_for_assignment: boolean;
  can_unignore_for_assignment: boolean;
}

export function messagingConversationProviderLabel(provider: MessagingConversation['provider']): string {
  switch (provider) {
    case 'slack':
      return 'Slack';
    case 'teams':
      return 'Teams';
    case 'google_chat':
      return 'Google Chat';
  }
}

export function isMessagingConversationPersonal(conversation: MessagingConversation): boolean {
  if (conversation.provider === 'teams') {
    return conversation.teams_conversation_type === 'one_on_one';
  }

  return conversation.google_chat_space_type === 'DIRECT_MESSAGE';
}

export function messagingConversationSpaceTypeKey(conversation: MessagingConversation): string {
  if (conversation.provider === 'teams') {
    return conversation.teams_conversation_type ?? 'teams';
  }

  return conversation.google_chat_space_type ?? conversation.provider;
}

export function messagingConversationSpaceTypeLabel(conversation: MessagingConversation): string {
  if (conversation.provider === 'teams') {
    switch (conversation.teams_conversation_type) {
      case 'one_on_one':
        return 'Personal';
      case 'group_chat':
        return 'Grupo';
      case 'channel':
        return 'Canal';
      default:
        return 'Teams';
    }
  }

  return isMessagingConversationPersonal(conversation) ? 'Personal' : 'Grupo';
}
